import { useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Pressable,
  ActivityIndicator,
  Alert,
  Platform,
  Image,
  KeyboardAvoidingView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { validateDisplayNameFormat } from '@/lib/usernameValidation';
import { validateBodyweightChange } from '@/lib/antiGaming';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import * as ImagePicker from 'expo-image-picker';
import { Camera, MapPin, ChevronRight, Check } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useUnits } from '@/lib/useUnits';
import { convertWeight } from '@/lib/volumeCalc';

export default function EditProfileScreen() {
  const router = useRouter();
  const { profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const { displayWeight } = useUnits();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [gender, setGender] = useState<string>((profile as any)?.gender || '');
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState(() => {
    const dob = (profile as any)?.date_of_birth;
    if (!dob) return '';
    return new Date(dob).getFullYear().toString();
  });
  const [bodyweight, setBodyweight] = useState(profile?.bodyweight?.toString() || '');
  const [bodyFat, setBodyFat] = useState(profile?.body_fat && profile.body_fat > 0 ? profile.body_fat.toString() : '');
  const [units, setUnits] = useState<'lbs' | 'kg'>(profile?.units || 'lbs');
  const [trainingGoals, setTrainingGoals] = useState<string[]>(
    (profile as any)?.training_goals ?? []
  );
  const [heightCm, setHeightCm] = useState(profile?.height_cm?.toString() || '');
  const [heightUnit, setHeightUnit] = useState<'cm' | 'ftin'>('ftin');
  const [heightFt, setHeightFt] = useState(() => {
    if (!profile?.height_cm) return '';
    const totalIn = Math.round(profile.height_cm / 2.54);
    return Math.floor(totalIn / 12).toString();
  });
  const [heightIn, setHeightIn] = useState(() => {
    if (!profile?.height_cm) return '';
    const totalIn = Math.round(profile.height_cm / 2.54);
    return (totalIn % 12).toString();
  });
  const [phone, setPhone] = useState(profile?.phone || '');
  const [addressLine1, setAddressLine1] = useState(profile?.address_line1 || '');
  const [addressCity, setAddressCity] = useState(profile?.address_city || '');
  const [addressState, setAddressState] = useState(profile?.address_state || '');
  const [addressZip, setAddressZip] = useState(profile?.address_zip || '');
  const [bodyweightError, setBodyweightError] = useState('');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const TRAINING_GOAL_OPTIONS = [
    'Strength Training',
    'Powerlifting',
    'Hypertrophy',
    'Muscular Endurance',
  ] as const;

  // Compute height_cm from ft/in or cm depending on unit
  const computeHeightCm = useCallback((overrides: Record<string, any> = {}): number | null => {
    const unit = overrides.height_unit ?? heightUnit;
    if (unit === 'cm') {
      const val = overrides.height_cm ?? heightCm;
      return val ? parseFloat(val) : null;
    }
    const ft = parseFloat(overrides.height_ft ?? heightFt);
    const inches = parseFloat(overrides.height_in ?? heightIn);
    if (isNaN(ft) && isNaN(inches)) return null;
    const totalInches = (isNaN(ft) ? 0 : ft * 12) + (isNaN(inches) ? 0 : inches);
    return Math.round(totalInches * 2.54 * 10) / 10;
  }, [heightUnit, heightCm, heightFt, heightIn]);

  // Auto-save with debounce
  const persistProfile = useCallback(async (overrides: Record<string, any> = {}) => {
    if (!profile) return;
    const currentDisplayName = overrides.display_name ?? displayName.trim();
    const currentBio = overrides.bio ?? bio.trim();
    const currentBodyweight = overrides.bodyweight ?? bodyweight;
    const currentBodyFat = overrides.body_fat ?? bodyFat;
    const currentUnits = overrides.units ?? units;
    const currentGoals = overrides.training_goals ?? trainingGoals;

    if (!currentDisplayName) return; // don't save empty name

    const dispValidation = validateDisplayNameFormat(currentDisplayName);
    if (!dispValidation.isValid) return;

    if (currentBodyweight && parseFloat(currentBodyweight) > 0) {
      const bw = parseFloat(currentBodyweight);
      if (isNaN(bw) || bw < 30 || bw > 700) return;
    }
    if (currentBodyFat && parseFloat(currentBodyFat) > 0) {
      const bf = parseFloat(currentBodyFat);
      if (isNaN(bf) || bf < 1 || bf > 60) return;
    }

    setSaving(true);
    setSaved(false);
    try {
      const currentHeightCm = computeHeightCm(overrides);
      const currentPhone = overrides.phone ?? phone;
      const currentAddressLine1 = overrides.address_line1 ?? addressLine1;
      const currentAddressCity = overrides.address_city ?? addressCity;
      const currentAddressState = overrides.address_state ?? addressState;
      const currentAddressZip = overrides.address_zip ?? addressZip;

      // Bodyweight velocity check
      if (currentBodyweight && parseFloat(currentBodyweight) > 0) {
        const newBwLbs = currentUnits === 'kg'
          ? parseFloat(currentBodyweight) * 2.20462
          : parseFloat(currentBodyweight);
        const prevBwLbs = profile.units === 'kg'
          ? (profile.bodyweight ?? 0) * 2.20462
          : (profile.bodyweight ?? 0);
        const lastBwChangedAt = (profile as any).bw_changed_at ?? null;
        const bwResult = validateBodyweightChange(newBwLbs, prevBwLbs, lastBwChangedAt);
        if (!bwResult.allowed) {
          Alert.alert('Bodyweight Change Blocked', bwResult.flag?.message || 'Change too large. Contact support.');
          setSaving(false);
          return;
        }
      }

      // Core fields that definitely exist in the profiles table
      const updates: Record<string, any> = {
        bio: currentBio || null,
        bodyweight: currentBodyweight ? parseFloat(currentBodyweight) : null,
        units: currentUnits,
        training_goals: currentGoals.length > 0 ? currentGoals : null,
      };
      // Only include display_name if it actually changed (immutable constraint in DB)
      if (currentDisplayName !== profile.display_name) {
        updates.display_name = currentDisplayName;
      }

      // Optional fields — save separately so missing columns don't block the main update
      const optionalFields: Record<string, any> = {};
      if (currentBodyFat && parseFloat(currentBodyFat) > 0) optionalFields.body_fat = parseFloat(currentBodyFat);
      if (currentHeightCm) optionalFields.height_cm = currentHeightCm;
      if (gender) optionalFields.gender = gender;
      // Build DOB from year if provided (month/day optional — we store year for age bracket)
      if (dobYear && parseInt(dobYear) > 1900 && parseInt(dobYear) <= new Date().getFullYear() - 13) {
        const month = dobMonth || '01';
        const day = dobDay || '01';
        optionalFields.date_of_birth = `${dobYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      if (currentPhone) optionalFields.phone = currentPhone;
      if (currentAddressLine1) optionalFields.address_line1 = currentAddressLine1;
      if (currentAddressCity) optionalFields.address_city = currentAddressCity;
      if (currentAddressState) optionalFields.address_state = currentAddressState;
      if (currentAddressZip) optionalFields.address_zip = currentAddressZip;

      // Try optional fields separately — silently skip if columns don't exist
      if (Object.keys(optionalFields).length > 0) {
        supabase.from('profiles').update(optionalFields).eq('id', profile.id).then(({ error: optErr }) => {
          if (optErr) console.warn('[EDIT-PROFILE] Optional fields save skipped:', optErr.message);
        });
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile.id);

      if (error) {
        console.error('[EDIT-PROFILE] Save failed:', error.message, error.code, error.details);
        throw error;
      }
      await refreshProfile(true);  // Force refresh to bypass debounce after save
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      console.error('[EDIT-PROFILE] Save error:', err?.message ?? err);
    } finally {
      setSaving(false);
    }
  }, [profile, displayName, bio, bodyweight, bodyFat, units, trainingGoals, refreshProfile, computeHeightCm, phone, addressLine1, addressCity, addressState, addressZip]);

  const debouncedSave = useCallback((overrides: Record<string, any> = {}) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistProfile(overrides), 800);
  }, [persistProfile]);

  // Immediate save (for toggles like units)
  const immediateSave = useCallback((overrides: Record<string, any> = {}) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    persistProfile(overrides);
  }, [persistProfile]);

  const handleBodyweightChange = useCallback((value: string) => {
    setBodyweight(value);
    if (value && parseFloat(value) <= 0) {
      setBodyweightError('Please enter your bodyweight');
    } else {
      setBodyweightError('');
    }
    debouncedSave({ bodyweight: value });
  }, [debouncedSave]);

  const handleUnitsChange = (newUnits: 'lbs' | 'kg') => {
    const oldUnits = units;
    setUnits(newUnits);
    
    // Convert bodyweight value when switching units
    if (bodyweight && oldUnits !== newUnits) {
      const currentWeight = parseFloat(bodyweight);
      if (!isNaN(currentWeight)) {
        const convertedWeight = convertWeight(currentWeight, oldUnits, newUnits);
        const formattedWeight = Math.round(convertedWeight * 10) / 10;
        setBodyweight(formattedWeight.toString());
      }
    }
    
    // Save with the converted bodyweight too
    const saveOverrides: Record<string, any> = { units: newUnits };
    if (bodyweight) {
      const bw = parseFloat(bodyweight);
      if (!isNaN(bw) && bw > 0) {
        const converted = convertWeight(bw, units, newUnits);
        saveOverrides.bodyweight = Math.round(converted * 10) / 10;
      }
    }
    immediateSave(saveOverrides);
  };

  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const pickImage = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (result.canceled || !result.assets?.[0]) return;
      if (!profile?.id) return;

      setUploadingPhoto(true);
      const asset = result.assets[0];

      // On web, blob URIs don't have real extensions — use mimeType or default to jpg
      let fileExt = 'jpg';
      let contentType = 'image/jpeg';
      if (asset.mimeType) {
        contentType = asset.mimeType;
        fileExt = asset.mimeType.split('/')[1] || 'jpg';
      } else if (!asset.uri.startsWith('blob:') && !asset.uri.startsWith('data:')) {
        const uriExt = asset.uri.split('.').pop()?.toLowerCase();
        if (uriExt && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(uriExt)) {
          fileExt = uriExt;
          contentType = `image/${uriExt === 'jpg' ? 'jpeg' : uriExt}`;
        }
      }
      const filePath = `${profile.id}/avatar.${fileExt}`;

      // Read image — use expo-file-system on native for reliable base64 encoding
      // fetch().blob() can return empty blobs on some iOS/Android versions
      let fileBody: Blob | Uint8Array;
      if (Platform.OS !== 'web') {
        const FileSystem = await import('expo-file-system');
        const b64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' as any });
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        fileBody = bytes;
      } else {
        const response = await fetch(asset.uri);
        fileBody = await response.blob();
      }

      // Content moderation — check image before uploading
      try {
        const openAiKey = process.env.EXPO_PUBLIC_OPENAI_API_KEY;
        if (openAiKey) {
          // Use the local file URI directly as a public-accessible URL won't exist yet.
          // On native, read base64 via expo-file-system.
          let imageDataUrl: string | null = null;
          if (Platform.OS !== 'web') {
            const FileSystem = await import('expo-file-system');
            const b64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' as any });
            imageDataUrl = `data:${contentType};base64,${b64}`;
          }
          if (imageDataUrl) {
            const modResponse = await fetch('https://api.openai.com/v1/moderations', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${openAiKey}` },
              body: JSON.stringify({
                model: 'omni-moderation-latest',
                input: [{ type: 'image_url', image_url: { url: imageDataUrl } }],
              }),
            });
            if (modResponse.ok) {
              const modData = await modResponse.json();
              if (modData.results?.[0]?.flagged) {
                setUploadingPhoto(false);
                Alert.alert('Image Not Allowed', 'This image violates our community guidelines and cannot be used as a profile photo.');
                return;
              }
            }
          }
        }
        // If no API key or moderation fails, proceed (fail open)
      } catch { /* non-blocking */ }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, fileBody, { upsert: true, contentType });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`; // cache bust

      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', profile.id);

      if (profileUpdateError) throw profileUpdateError;

      // force=true bypasses the 2s debounce so the new avatar_url is read immediately
      await refreshProfile(true);

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      console.error('[PHOTO UPLOAD]', err);
      const friendlyMsg = "Couldn't upload your photo. Please try again.";
      if (Platform.OS === 'web') {
        alert(friendlyMsg);
      } else {
        Alert.alert('Upload Failed', friendlyMsg);
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <SafeAreaView style={{ backgroundColor: theme.colors.background, flex: 1 }} edges={['top']}>
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
    <View style={[styles.container]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={{ width: 24 }} />
        <Text style={[styles.headerTitle, { color: theme.colors.accent }]} accessibilityRole="header">PROFILE</Text>
        <View style={{ width: 24 }} />
      </LinearGradient>

      <ScrollView contentContainerStyle={styles.content} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
        <View style={styles.avatarSection}>
          {profile?.avatar_url ? (
            <Image source={{ uri: profile.avatar_url }} style={styles.avatar} accessibilityLabel="Your profile photo" />
          ) : (
            <View style={[styles.avatar, { backgroundColor: theme.colors.accent }]}>
              <Text style={[styles.avatarText, { color: theme.colors.accentForeground }]}>
                {displayName.charAt(0).toUpperCase() || 'U'}
              </Text>
            </View>
          )}
          <TouchableOpacity
            style={[styles.changePhotoButton, { backgroundColor: theme.colors.surfaceVariant }]}
            onPress={pickImage}
            disabled={uploadingPhoto}
            accessibilityRole="button"
            accessibilityLabel="Change profile photo">
            {uploadingPhoto ? (
              <ActivityIndicator size="small" color={theme.colors.accent} />
            ) : (
              <Camera size={16} color={theme.colors.textSecondary} />
            )}
            <Text style={[styles.changePhotoText, { color: theme.colors.textSecondary }]}>
              {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.section}>
          <Text style={[styles.label, { color: theme.colors.text }]}>Display Name</Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
                color: theme.colors.text,
              },
            ]}
            value={displayName}
            onChangeText={(v) => { setDisplayName(v); debouncedSave({ display_name: v.trim() }); }}
            placeholder="Enter your display name"
            placeholderTextColor={theme.colors.textTertiary}
            accessibilityLabel="Display name"
          />
        </View>

        <View style={styles.section}>
          <Text style={[styles.label, { color: theme.colors.text }]}>Bio</Text>
          <TextInput
            style={[
              styles.input,
              styles.textArea,
              {
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
                color: theme.colors.text,
              },
            ]}
            value={bio}
            onChangeText={(v) => { setBio(v); debouncedSave({ bio: v.trim() }); }}
            placeholder="Tell others about yourself..."
            placeholderTextColor={theme.colors.textTertiary}
            multiline
            numberOfLines={4}
            accessibilityLabel="Bio"
          />
        </View>

        <View style={styles.section}>
          <Text style={[styles.label, { color: theme.colors.text }]}>Training Goals</Text>
          <View style={styles.goalsContainer}>
            {TRAINING_GOAL_OPTIONS.map((goal) => {
              const isSelected = trainingGoals.includes(goal);
              return (
                <TouchableOpacity
                  key={goal}
                  style={[
                    styles.goalPill,
                    {
                      backgroundColor: isSelected ? theme.colors.accent : theme.colors.surface,
                      borderColor: isSelected ? theme.colors.accent : theme.colors.border,
                    },
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={goal}
                  accessibilityState={{ selected: isSelected }}
                  onPress={() => {
                    const next = isSelected
                      ? trainingGoals.filter((g) => g !== goal)
                      : [...trainingGoals, goal];
                    setTrainingGoals(next);
                    immediateSave({ training_goals: next });
                  }}>
                  <Text
                    style={[
                      styles.goalPillText,
                      { color: isSelected ? theme.colors.text : theme.colors.textSecondary },
                    ]}>
                    {goal}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={[styles.label, { color: theme.colors.text }]}>Units</Text>
          <View style={styles.unitsContainer}>
            <TouchableOpacity
              style={[
                styles.unitButton,
                {
                  backgroundColor:
                    units === 'lbs' ? theme.colors.accent : theme.colors.surfaceVariant,
                  borderColor: theme.colors.accent,
                },
              ]}
              onPress={() => handleUnitsChange('lbs')}
              accessibilityRole="button"
              accessibilityLabel="Pounds"
              accessibilityState={{ selected: units === 'lbs' }}>
              <Text
                style={[
                  styles.unitButtonText,
                  { color: units === 'lbs' ? theme.colors.text : theme.colors.textSecondary },
                ]}>
                lbs
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.unitButton,
                {
                  backgroundColor:
                    units === 'kg' ? theme.colors.accent : theme.colors.surfaceVariant,
                  borderColor: theme.colors.accent,
                },
              ]}
              onPress={() => handleUnitsChange('kg')}
              accessibilityRole="button"
              accessibilityLabel="Kilograms"
              accessibilityState={{ selected: units === 'kg' }}>
              <Text
                style={[
                  styles.unitButtonText,
                  { color: units === 'kg' ? theme.colors.text : theme.colors.textSecondary },
                ]}>
                kg
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Gender — locked after onboarding, affects weight class and DOTS scoring */}
        {gender ? (
          <View style={styles.section}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Gender</Text>
            <View style={[styles.input, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12 }]}>
              <Text style={{ color: theme.colors.text, fontWeight: '600', textTransform: 'capitalize' }}>{gender}</Text>
              <Text style={{ color: theme.colors.textTertiary, fontSize: 11 }}>Cannot be changed</Text>
            </View>
          </View>
        ) : null}

        {/* Date of Birth — birth year for age bracket */}
        <View style={styles.section}>
          <Text style={[styles.label, { color: theme.colors.text }]}>Birth Year</Text>
          <TextInput
            style={[styles.input, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]}
            value={dobYear}
            onChangeText={(v) => {
              setDobYear(v);
              if (v.length === 4 && parseInt(v) > 1900) immediateSave({ date_of_birth: `${v}-01-01` });
            }}
            placeholder="e.g. 1990"
            placeholderTextColor={theme.colors.textTertiary}
            keyboardType="number-pad"
            maxLength={4}
            accessibilityLabel="Birth year"
          />
          <Text style={{ fontSize: 11, color: theme.colors.textTertiary, marginTop: 4 }}>Used for age bracket ranking. Not shown publicly.</Text>
        </View>

        <View style={styles.row}>
          <View style={[styles.section, { flex: 1 }]}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Bodyweight ({units})</Text>
            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: bodyweightError ? (theme.colors.error || '#ff4444') : theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              value={bodyweight}
              onChangeText={handleBodyweightChange}
              placeholder="0"
              placeholderTextColor={theme.colors.textTertiary}
              keyboardType="decimal-pad"
              accessibilityLabel="Bodyweight"
            />
            {bodyweightError ? (
              <Text style={[styles.errorText, { color: theme.colors.error || '#ff4444', fontSize: 12, marginTop: 4 }]}>{bodyweightError}</Text>
            ) : null}
          </View>

          <View style={[styles.section, { flex: 1 }]}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Body Fat %</Text>
            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              value={bodyFat}
              onChangeText={(v) => { setBodyFat(v); debouncedSave({ body_fat: v }); }}
              placeholder="0"
              placeholderTextColor={theme.colors.textTertiary}
              keyboardType="decimal-pad"
              accessibilityLabel="Body fat percentage"
            />
          </View>
        </View>

        {/* Personal Information */}
        <View style={[styles.section, { marginTop: 12 }]}>
          <Text style={[styles.label, { color: theme.colors.accent, fontSize: 16, marginBottom: 16 }]}>Personal Information</Text>

          <Text style={[styles.label, { color: theme.colors.text }]}>Height</Text>
          <View style={[styles.unitsContainer, { marginBottom: 8 }]}>
            <TouchableOpacity
              style={[styles.unitButton, { backgroundColor: heightUnit === 'ftin' ? theme.colors.accent : theme.colors.surfaceVariant, borderColor: theme.colors.accent }]}
              onPress={() => {
                // Convert cm to ft/in when switching
                if (heightUnit === 'cm' && heightCm) {
                  const cm = parseFloat(heightCm);
                  if (!isNaN(cm) && cm > 0) {
                    const totalIn = Math.round(cm / 2.54);
                    setHeightFt(Math.floor(totalIn / 12).toString());
                    setHeightIn((totalIn % 12).toString());
                  }
                }
                setHeightUnit('ftin');
              }}
              accessibilityRole="button"
              accessibilityState={{ selected: heightUnit === 'ftin' }}>
              <Text style={[styles.unitButtonText, { color: heightUnit === 'ftin' ? theme.colors.text : theme.colors.textSecondary }]}>ft/in</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.unitButton, { backgroundColor: heightUnit === 'cm' ? theme.colors.accent : theme.colors.surfaceVariant, borderColor: theme.colors.accent }]}
              onPress={() => {
                // Convert ft/in to cm when switching
                if (heightUnit === 'ftin' && (heightFt || heightIn)) {
                  const ft = parseInt(heightFt) || 0;
                  const inches = parseInt(heightIn) || 0;
                  const cm = Math.round((ft * 12 + inches) * 2.54);
                  if (cm > 0) setHeightCm(cm.toString());
                }
                setHeightUnit('cm');
              }}
              accessibilityRole="button"
              accessibilityState={{ selected: heightUnit === 'cm' }}>
              <Text style={[styles.unitButtonText, { color: heightUnit === 'cm' ? theme.colors.text : theme.colors.textSecondary }]}>cm</Text>
            </TouchableOpacity>
          </View>
          {heightUnit === 'ftin' ? (
            <View style={styles.row}>
              <View style={{ flex: 1 }}>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]}
                  value={heightFt}
                  onChangeText={(v) => { setHeightFt(v); debouncedSave({ height_ft: v }); }}
                  placeholder="ft"
                  placeholderTextColor={theme.colors.textTertiary}
                  keyboardType="number-pad"
                  accessibilityLabel="Height feet"
                />
              </View>
              <View style={{ flex: 1 }}>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]}
                  value={heightIn}
                  onChangeText={(v) => { setHeightIn(v); debouncedSave({ height_in: v }); }}
                  placeholder="in"
                  placeholderTextColor={theme.colors.textTertiary}
                  keyboardType="number-pad"
                  accessibilityLabel="Height inches"
                />
              </View>
            </View>
          ) : (
            <TextInput
              style={[styles.input, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, color: theme.colors.text }]}
              value={heightCm}
              onChangeText={(v) => { setHeightCm(v); debouncedSave({ height_cm: v }); }}
              placeholder="Height in cm"
              placeholderTextColor={theme.colors.textTertiary}
              keyboardType="decimal-pad"
              accessibilityLabel="Height in centimeters"
            />
          )}
        </View>

        {/* Gym management moved to Settings → My Gyms */}

        {/* Auto-save status indicator */}
        <View style={styles.saveStatus}>
          {saving && (
            <View style={styles.saveStatusRow}>
              <ActivityIndicator size="small" color={theme.colors.textTertiary} />
              <Text style={[styles.saveStatusText, { color: theme.colors.textTertiary }]}>Saving…</Text>
            </View>
          )}
          {saved && !saving && (
            <View style={styles.saveStatusRow}>
              <Check size={14} color={theme.colors.accent} />
              <Text style={[styles.saveStatusText, { color: theme.colors.accent }]}>Saved</Text>
            </View>
          )}
        </View>
      </ScrollView>

    </View>
    </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 1.5,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  avatarSection: {
    alignItems: 'center',
    marginBottom: 32,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  avatarText: {
    fontSize: 40,
    fontWeight: '900',
  },
  changePhotoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 0,
  },
  changePhotoText: {
    fontSize: 14,
    fontWeight: '600',
  },
  section: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  badgeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 0,
    borderWidth: 1,
  },
  badgeButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  helperText: {
    fontSize: 12,
    marginTop: 6,
  },
  unitsContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  unitButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: 'center',
  },
  unitButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  homeGymInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 2,
    borderWidth: 1,
    marginBottom: 8,
  },
  homeGymDetails: {
    flex: 1,
  },
  homeGymName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  homeGymAddress: {
    fontSize: 13,
  },
  gymActions: {
    gap: 8,
  },
  changeGymButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 0,
    borderWidth: 1,
  },
  changeGymText: {
    fontSize: 15,
    fontWeight: '600',
  },
  viewGymButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 0,
    borderWidth: 2,
  },
  viewGymText: {
    fontSize: 15,
    fontWeight: '700',
  },
  hint: {
    fontSize: 12,
    marginTop: 6,
  },
  saveStatus: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    minHeight: 48,
  },
  saveStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  saveStatusText: {
    fontSize: 13,
    fontWeight: '600',
  },
  goalsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  goalPill: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 0,
    borderWidth: 1,
  },
  goalPillText: {
    fontSize: 13,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 12,
    fontWeight: '500',
  },
});
